/* eslint-disable import/extensions */
/* eslint-disable react/jsx-filename-extension */
/* eslint-disable max-len */

import React from 'react';
import { useParams, Link } from 'react-router-dom';

import Industries from 'data/industries';
import { Fade } from 'react-awesome-reveal';
import Discuss from 'parts/Discuss';
import slugify from '../../utils/slugify';

/**
 * IndustryDetailPage
 * /industries/:slug
 * Matches layout of your service detail pages
 */
export default function IndustryDetailPage() {
  const { slug } = useParams();
  const index = Industries.findIndex((name) => slugify(name) === slug);
  const name = index !== -1 ? Industries[index] : null;

  // LOAD IMAGE IF EXISTS
  // Use exact filenames for specific cases, else fallback to standard slug jpg
  let imgFilename = `${slug}.jpg`;
  if (slug === 'manufacturing-and-logistics') {
    imgFilename = 'manufacturing-and-logistics.jpg';
  }
  if (name === 'Aviation') {
    imgFilename = 'Aviation.jpg'; // capitalized
  }
  const imgSrc = `/hero/industries/${imgFilename}`;

  if (!name) {
    return (
      <section className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-4xl font-bold text-surface-900 mb-4 tracking-tight">Industry Not Found</h1>
        <p className="text-surface-300 mb-6">The industry you were looking for does not exist.</p>
        <Link to="/industries" className="text-brand-green-500 hover:text-brand-green-600 transition-colors font-medium">Back to Industries</Link>
      </section>
    );
  }

  const description = `${name} organizations benefit heavily from tailored IT solutions — from cybersecurity and cloud infrastructure to compliance and business continuity. Explore how our services support the ${name.toLowerCase()} sector.`;

  return (
    <>

      {/* HERO */}
      <section className="py-20 lg:py-28 bg-premium-light">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            
            <div className="lg:w-1/2">
              <Fade direction="up" triggerOnce>
                <div className="mb-4">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-brand-green-50 text-brand-green-600 text-sm font-semibold tracking-wide uppercase">
                    Industry Verticals
                  </span>
                </div>
                <h1 className="text-4xl lg:text-5xl font-extrabold text-surface-900 mb-6 tracking-tight">
                  {name}
                </h1>
                <p className="text-surface-300 text-lg mb-8 leading-relaxed">
                  {description}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/services" className="btn-premium">
                    View Services
                  </Link>
                  <Link to="/contact" className="px-8 py-4 bg-white text-surface-900 border border-surface-200 rounded-lg hover:border-surface-300 hover:bg-surface-50 transition-all font-semibold shadow-sm">
                    Contact Us
                  </Link>
                </div>
              </Fade>
            </div>

            <div className="lg:w-1/2 flex items-center justify-center w-full">
              <Fade direction="up" delay={200} triggerOnce className="w-full">
                {imgSrc ? (
                  <div className="relative rounded-2xl overflow-hidden glass-card-static w-full h-[300px] lg:h-[400px]">
                    <img src={imgSrc} alt={name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-surface-900/40 to-transparent" />
                  </div>
                ) : (
                  <div className="w-full h-[300px] lg:h-[400px] bg-surface-900/5 backdrop-blur-sm rounded-2xl flex items-center justify-center glass-card-static">
                    <svg className="w-24 h-24 text-brand-green-500/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                )}
              </Fade>
            </div>

          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* LEFT COLUMN */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-extrabold text-surface-900 mb-6 tracking-tight">
              How We Support the {name} Industry
            </h2>

            <p className="text-surface-300 text-lg mb-8 leading-relaxed">
              We design IT strategies that support the mission and operations of {name.toLowerCase()} organizations—
              helping them scale, secure their data, improve uptime, and achieve compliance requirements.
            </p>

            <div className="glass-card-static p-8 rounded-2xl mb-8">
              <h3 className="text-xl font-bold text-surface-900 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-brand-green-50 text-brand-green-600 flex items-center justify-center">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                What You’ll Gain
              </h3>

              <ul className="space-y-4">
                {[
                  "Improved uptime, reliability, and performance",
                  "Stronger cybersecurity posture",
                  "Optimized cloud and infrastructure costs",
                  "Better business continuity and disaster readiness",
                ].map((item) => (
                  <li key={item} className="flex gap-4 items-start">
                    <span className="mt-1 w-2 h-2 rounded-full bg-brand-green-500 shrink-0" />
                    <span className="text-surface-300 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <aside className="space-y-8">
            <div className="glass-card-static rounded-2xl p-6 border-t-4 border-brand-green-500">
              <h4 className="text-lg font-bold text-surface-900 mb-4 tracking-tight">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/services/managed-it-services" className="text-surface-300 hover:text-brand-green-500 transition-colors font-medium flex items-center justify-between group">Managed IT Services <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span></Link></li>
                <li><Link to="/services/network-security" className="text-surface-300 hover:text-brand-green-500 transition-colors font-medium flex items-center justify-between group">Network Security <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span></Link></li>
                <li><Link to="/services/cloud-solutions" className="text-surface-300 hover:text-brand-green-500 transition-colors font-medium flex items-center justify-between group">Cloud Solutions <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span></Link></li>
              </ul>
            </div>
            
            <div className="bg-surface-900 rounded-2xl p-6 text-white">
              <h4 className="text-lg font-bold mb-2">Need Expert Help?</h4>
              <p className="text-surface-300 text-sm mb-6">Talk to our experts about specialized solutions for your industry.</p>
              <Link to="/contact" className="block w-full py-3 bg-brand-green-500 hover:bg-brand-green-600 text-white font-semibold rounded-lg text-center transition-colors">
                Contact Sales
              </Link>
            </div>
          </aside>

        </div>
      </section>

      <Discuss />
    </>
  );
}
